/**
 * Admin fetch wrapper.
 *
 * Different from the dashboard's api.ts in ONE way: it hits an
 * absolute origin (https://api.pfh.trading) because the admin SPA is
 * served from admin.pfh.trading — cookies work cross-subdomain but
 * CORS does not assume same-origin.
 *
 * Dev: Vite proxies /api/* to http://localhost:8787 on port 5174,
 * so in dev the origin is a relative /api path.
 */

const API_ORIGIN =
  import.meta.env.DEV ? "" : "https://api.pfh.trading";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body: unknown = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${API_ORIGIN}${path}`;
  const res = await fetch(url, {
    credentials: "include",
    ...init,
    headers: {
      accept: "application/json",
      "content-type": "application/json",
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, `${init.method ?? "GET"} ${path} → ${res.status}`, body);
  }
  if (res.status === 204) return null as T;
  try {
    return (await res.json()) as T;
  } catch (err) {
    throw new ApiError(
      res.status,
      `${init.method ?? "GET"} ${path} → invalid JSON in ${res.status} response`,
      { parseError: err instanceof Error ? err.message : String(err) },
    );
  }
}

export const api = {
  get: <T>(path: string) => call<T>(path),
  post: <T>(path: string, body: unknown) =>
    call<T>(path, { method: "POST", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    call<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  del: <T>(path: string) => call<T>(path, { method: "DELETE" }),
};
